import { Sort } from "./DataSource";
import {
  AccessContext,
  DynamicField,
  Field,
  FunWithCtxOr,
  mapDynamicToStaticField,
  mergeCtx,
  withDefaults
} from "./Field";
import { filter, groupBy, isNil } from "lodash";
import { LoggerFactory } from "../logger";

const log = LoggerFactory.create("Schema");

export interface SchemaOptions<T> {
  fields: (T | null)[];
  primaryKey: string[];
  defaultSort?: Sort;
  groups?: Groups;
  security:
    | string
    | {
        readableRole: string | string[];
        editableRole: string | string[];
      };
  context?: AccessContext;
}

export interface Groups {
  [groupName: string]: FunWithCtxOr<string>;
}

// false works as exclude flag for field
export interface SchemaFieldsExtender<T> {
  [fieldName: string]: Partial<T> | false;
}

export interface SchemaExtendOptions {
  onlyGiven?: boolean;
  groups?: Groups;
}

export interface WithFields<
  T extends DynamicField = DynamicField,
  M extends Field = Field
> {
  fields: T[];
  mapFields<R>(
    fn: (field: M, idx: number) => R,
    ctx?: Partial<AccessContext>
  ): R[];
}

export function evaluateWithContext(
  ctx: Partial<AccessContext>,
  it: FunWithCtxOr<any> | undefined
): any {
  if (typeof it === "function") {
    return it(ctx);
  }
  return it;
}

export function checkGroupVisibility(groupSchema: Schema, data: any): boolean {
  const mappedFields = groupSchema.mapFields(
    field => {
      return field;
    },
    { data }
  );
  return mappedFields.length > 0;
}

export class Schema<
  T extends DynamicField | Field = DynamicField,
  M extends Field = Field
> implements WithFields<T, M> {
  public fields: T[];
  public groups: Groups;
  public primaryKey: string[];
  public Named: { [name: string]: Field };
  public defaultSort?: Sort;
  public context?: AccessContext;
  public security: {
    readableRole: string | string[];
    editableRole: string | string[];
  };

  public constructor({
    fields,
    groups,
    primaryKey,
    security,
    defaultSort,
    context
  }: SchemaOptions<T>) {
    this.fields = withDefaults(fields.filter(field => field !== null) as T[]);
    this.groups = groups || {};
    this.primaryKey = primaryKey;
    this.defaultSort = defaultSort;
    this.security =
      typeof security === "string"
        ? {
            readableRole: `${security}_read`,
            editableRole: `${security}_write`
          }
        : security;
    this.Named = this.fields.reduce((acc, field) => {
      const f = field as Field;
      acc[f.name] = field;
      return acc;
    }, {});
    this.context = context;
  }

  public mapFields<R>(
    fn: (field: M, idx: number) => R,
    ctx?: Partial<AccessContext>
  ): R[] {
    const mergedCtx = mergeCtx(this.context, ctx);
    return this.fields
      .map((field: T, idx: number) => {
        return mapDynamicToStaticField(field, mergedCtx);
      })
      .filter((f: T) => f !== null)
      .map(fn);
  }

  public toId(data: {}): {} {
    return this.primaryKey.reduce((obj, key) => {
      obj[key] = data[key];
      return obj;
    }, {});
  }

  public link(data: {}): string {
    return "/" + this.primaryKeyString(data);
  }

  public primaryKeyString(data: {}): string {
    return this.primaryKey
      .filter(key => !isNil(data[key]))
      .map(key => {
        return encodeURI(data[key]);
      })
      .join("+");
  }

  public makeAllReadonly<U extends Field>(): Schema<U> {
    const newFields: U[] = Object.keys(this.Named).reduce((acc, field) => {
      if (!this.Named[field].readonly) {
        acc.push(Object.assign({}, this.Named[field], { readonly: true }) as U);
      } else {
        acc.push(this.Named[field] as U);
      }
      return acc;
    }, [] as U[]);

    return new (this.constructor as SchemaConstructor<U>)({
      fields: newFields,
      context: this.context,
      primaryKey: this.primaryKey,
      security: this.security,
      groups: this.groups
    });
  }

  public extend<
    U extends T = T,
    W extends M = M,
    R extends Schema<U, W> = Schema<U, W>
  >(
    fieldsToMerge: SchemaFieldsExtender<U>,
    options: SchemaExtendOptions = { onlyGiven: false }
  ): R {
    const newFields: U[] = Object.keys(this.Named).reduce((acc, field) => {
      const ignore = options.onlyGiven && fieldsToMerge[field] === undefined;
      if (!ignore && fieldsToMerge[field] !== false) {
        acc.push(
          Object.assign({}, this.Named[field], fieldsToMerge[field]) as U
        );
      }
      return acc;
    }, [] as U[]);

    return new (this.constructor as SchemaConstructor<U, W, R>)({
      fields: newFields,
      context: this.context,
      primaryKey: this.primaryKey,
      security: this.security,
      groups: options.groups || this.groups
    });
  }

  public partitionByGroup(): { [name: string]: Schema<T, M> } {
    const map = {};
    const byGroup = groupBy(this.fields, "group");

    Object.entries(byGroup).forEach(([groupName, fields]) => {
      if (fields.length > 0) {
        map[groupName] = new (this.constructor as SchemaConstructor<T, M>)({
          fields: fields,
          defaultSort: this.defaultSort,
          primaryKey: this.primaryKey,
          context: this.context,
          security: this.security,
          groups: this.groups
        });
      }
    });

    log.debug("partitionByGroup", byGroup);
    log.debug("partitionByGroup =>", map);

    return map;
  }

  public pickGroups(...names: string[]): Schema<T, M> {
    const filteredFields = filter(
      this.fields,
      (f: T) => f.group && names.indexOf(f.group) !== -1
    ) as T[];

    return new (this.constructor as SchemaConstructor<T, M>)({
      context: this.context,
      defaultSort: this.defaultSort,
      fields: filteredFields,
      primaryKey: this.primaryKey,
      security: this.security,
      groups: this.groups
    });
  }
}

export interface SchemaConstructor<
  T extends DynamicField = DynamicField,
  S extends Field = Field,
  R extends Schema<T, S> = Schema<T, S>
> {
  new (options: SchemaOptions<T>): R;
}

export enum Language {
  ENGLISH = "EN",
  POLISH = "PL"
}
